import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  CreditBalance,
  CreditTransaction,
  TopupPack,
} from "../../../shared/types";

// ── Queries ──

export function useCreditBalance() {
  return useQuery({
    queryKey: ["credits"],
    queryFn: () => api.get<{ data: CreditBalance }>("/api/billing/credits"),
    // Poll every 30s to keep credit display fresh
    refetchInterval: 30_000,
  });
}

export function useUsageHistory() {
  return useQuery({
    queryKey: ["usage"],
    queryFn: () =>
      api.get<{ data: { transactions: CreditTransaction[]; total: number } }>(
        "/api/billing/usage",
      ),
  });
}

// ── Mutations ──

export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urls: { success_url: string; cancel_url: string }) =>
      api.post<{ data: { checkout_url: string; session_id: string } }>(
        "/api/billing/subscribe",
        urls,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credits"] });
    },
  });
}

export function useTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      pack: TopupPack;
      success_url: string;
      cancel_url: string;
    }) =>
      api.post<{ data: { checkout_url: string; session_id: string } }>(
        "/api/billing/topup",
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credits"] });
    },
  });
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: (return_url: string) =>
      api.post<{ data: { portal_url: string } }>("/api/billing/portal", {
        return_url,
      }),
  });
}
